import { useCallback, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"

import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { queryKeys } from "api/queryKeys"
import { projectMemberAdd, projectMemberRemove } from "api/Repos/projects"
import {
  BulkAction,
  BulkCall,
  BulkProject,
  BulkResult,
  MembersByProject,
  planBulkChanges,
  runBulkCalls,
} from "./bulkMembers"

/**
 * Runs a bulk add or remove of project members and keeps its progress and
 * results for the dialog.
 *
 * Calls are quiet: a sequence error (a body with "Err msg", or an empty body)
 * fails that call without notifying, so the user only sees the summary. The
 * shared member mutations are not used because they notify on every call.
 * Members lists of the projects called are invalidated after each run, once
 * the run shows as done (the refetch is not waited for).
 */

type BulkStatus = "idle" | "running" | "done"

const UNKNOWN_ERROR = "An unknown error occurred."

interface BulkProgress {
  done: number
  total: number
}

/** The sequence error in a reply body, if any (the rule of `handleSequenceError`, without notifying). */
const sequenceError = (body: unknown): string | undefined => {
  if (typeof body === "object" && body !== null && "Err msg" in body) {
    const msg = (body as Record<string, unknown>)["Err msg"]
    const text = msg === null || msg === undefined ? "" : String(msg)
    return text.trim() === "" ? UNKNOWN_ERROR : text
  }
  if (typeof body === "string" && body.trim() === "") {
    return UNKNOWN_ERROR
  }
  return undefined
}

const resultKey = (r: BulkCall): string => `${r.projectId}\n${r.person}`

/** Failed and not-run calls can run again; an unreadable project can't (nothing to plan from). */
export const isRetryable = (r: BulkResult): boolean =>
  r.outcome === "failed" || r.outcome === "not-run"

export const useBulkMembers = (): {
  status: BulkStatus
  progress: BulkProgress
  results: BulkResult[]
  start: (
    action: BulkAction,
    people: string[],
    projects: BulkProject[],
    members: MembersByProject
  ) => Promise<void>
  stop: () => void
  retryFailed: () => Promise<void>
  reset: () => void
} => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()

  const [status, setStatus] = useState<BulkStatus>("idle")
  const [progress, setProgress] = useState<BulkProgress>({ done: 0, total: 0 })
  const [results, setResults] = useState<BulkResult[]>([])

  // Refs so retry reads the latest run, and a second run can't start mid-run
  const resultsRef = useRef<BulkResult[]>([])
  const actionRef = useRef<BulkAction>("add")
  const signalRef = useRef({ stopped: false })
  const runningRef = useRef(false)

  const publish = useCallback((next: BulkResult[]): void => {
    resultsRef.current = next
    setResults(next)
  }, [])

  /** Runs `calls` and returns their results; the caller merges them in. */
  const run = useCallback(
    async (action: BulkAction, calls: BulkCall[]): Promise<BulkResult[]> => {
      const send = async ({ projectId, person }: BulkCall): Promise<void> => {
        const axios = await getAxios()
        const res =
          action === "add"
            ? await projectMemberAdd(axios, projectId, person)
            : await projectMemberRemove(axios, projectId, person)
        const error = sequenceError(res.data)
        if (error !== undefined) throw new Error(error)
      }

      signalRef.current = { stopped: false }
      setProgress({ done: 0, total: calls.length })
      setStatus("running")

      return runBulkCalls(calls, send, {
        signal: signalRef.current,
        onProgress: (done, total) => setProgress({ done, total }),
      })
    },
    [getAxios]
  )

  /** Refetches the members lists of the projects called, without waiting. */
  const invalidateTouched = useCallback(
    (ran: BulkResult[]): void => {
      const touched = new Set(
        ran.filter((r) => r.outcome !== "not-run").map((r) => r.projectId)
      )
      for (const id of touched) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.projects.members(id),
        })
      }
    },
    [queryClient]
  )

  const start = useCallback(
    async (
      action: BulkAction,
      people: string[],
      projects: BulkProject[],
      members: MembersByProject
    ): Promise<void> => {
      if (runningRef.current) return
      runningRef.current = true
      let ran: BulkResult[] = []
      try {
        actionRef.current = action
        const { calls, skipped } = planBulkChanges(
          action,
          people,
          projects,
          members
        )
        publish(skipped)
        ran = await run(action, calls)
        publish([...ran, ...skipped])
      } finally {
        runningRef.current = false
        setStatus("done")
      }
      invalidateTouched(ran)
    },
    [publish, run, invalidateTouched]
  )

  const retryFailed = useCallback(async (): Promise<void> => {
    if (runningRef.current) return
    const calls: BulkCall[] = resultsRef.current
      .filter(isRetryable)
      .map(({ projectId, projectName, person }) => ({
        projectId,
        projectName,
        person,
      }))
    if (calls.length === 0) return

    runningRef.current = true
    let ran: BulkResult[] = []
    try {
      ran = await run(actionRef.current, calls)
      const byKey = new Map(ran.map((r) => [resultKey(r), r]))
      publish(resultsRef.current.map((r) => byKey.get(resultKey(r)) ?? r))
    } finally {
      runningRef.current = false
      setStatus("done")
    }
    invalidateTouched(ran)
  }, [publish, run, invalidateTouched])

  const stop = useCallback((): void => {
    signalRef.current.stopped = true
  }, [])

  const reset = useCallback((): void => {
    if (runningRef.current) return
    publish([])
    setProgress({ done: 0, total: 0 })
    setStatus("idle")
  }, [publish])

  return { status, progress, results, start, stop, retryFailed, reset }
}
